import React, { useState, useEffect, useContext } from "react";
import { GlobalContext } from "../context/Provider";
import henceforthApi from "../utils/henceforthApi";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  Percent,
  Coins,
  Gift,
  Globe,
  Save,
  RotateCcw,
  CheckCircle2,
  Info,
  Sliders,
  Wallet,
  Receipt,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import "./Settings.scss";

interface CountrySetting {
  country_code: string;
  referral_amount: number | "";
  minimum_withdraw_amount: number | "";
}

interface SettingsData {
  admin_commission_percentage?: number | "";
  customer_without_referral_discount?: number | "";
  referral_person_first_ride_discount?: number | "";
  tax_percentage?: number | "";
  country_settings?: CountrySetting[];
  is_referral_coupon_credit_disable?: boolean;
}

interface CountryMeta {
  flag: string;
  name: string;
  currency: string;
  currencySymbol: string;
}

const COUNTRY_META: Record<string, CountryMeta> = {
  US: { flag: "🇺🇸", name: "United States", currency: "USD", currencySymbol: "$" },
  CA: { flag: "🇨🇦", name: "Canada", currency: "CAD", currencySymbol: "CA$" },
  NG: { flag: "🇳🇬", name: "Nigeria", currency: "NGN", currencySymbol: "₦" },
  IN: { flag: "🇮🇳", name: "India", currency: "INR", currencySymbol: "₹" },
  ROW: { flag: "🌐", name: "Rest of the World", currency: "USD", currencySymbol: "$" },
};

const DEFAULT_COUNTRY_SETTINGS: CountrySetting[] = [
  { country_code: "US", referral_amount: 10, minimum_withdraw_amount: 50 },
  { country_code: "CA", referral_amount: 10, minimum_withdraw_amount: 50 },
  { country_code: "NG", referral_amount: 10, minimum_withdraw_amount: 50 },
  { country_code: "IN", referral_amount: 10, minimum_withdraw_amount: 500 },
  { country_code: "ROW", referral_amount: 10, minimum_withdraw_amount: 50 },
];

const Settings = () => {
  const { authState, setLoading } = useContext(GlobalContext);
  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const [settings, setSettings] = useState<SettingsData>({});
  const [formData, setFormData] = useState<SettingsData>({
    admin_commission_percentage: "",
    customer_without_referral_discount: "",
    referral_person_first_ride_discount: "",
    tax_percentage: "",
    country_settings: DEFAULT_COUNTRY_SETTINGS,
    is_referral_coupon_credit_disable: false,
  });
  const [saving, setSaving] = useState(false);
  const [referralLoading, setReferralLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await henceforthApi.Settings.getSettings();
      const settingsData: SettingsData = res.data?.settings || {};
      setSettings(settingsData);

      const fetchedCountrySettings: CountrySetting[] = settingsData.country_settings || [];
      const mergedCountrySettings = DEFAULT_COUNTRY_SETTINGS.map((def) => {
        const found = fetchedCountrySettings.find((c) => c.country_code === def.country_code);
        return found ? { ...def, ...found } : def;
      });

      setFormData({
        admin_commission_percentage: settingsData.admin_commission_percentage ?? "",
        customer_without_referral_discount: settingsData.customer_without_referral_discount ?? "",
        referral_person_first_ride_discount: settingsData.referral_person_first_ride_discount ?? "",
        tax_percentage: settingsData.tax_percentage ?? "",
        country_settings: mergedCountrySettings,
        is_referral_coupon_credit_disable: settingsData.is_referral_coupon_credit_disable ?? false,
      });
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      Swal.fire({
        title: "Error!",
        text: error.response?.body?.error_description || "Failed to fetch settings",
        icon: "error",
        confirmButtonColor: "#006747",
        confirmButtonText: "OK",
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = value === "" ? "" : parseFloat(value);
    setFormData((prev) => ({ ...prev, [name]: numValue }));
  };

  const handleCountryChange = (
    country_code: string,
    field: "referral_amount" | "minimum_withdraw_amount",
    value: string
  ) => {
    const numValue = value === "" ? "" : parseFloat(value);
    setFormData((prev) => ({
      ...prev,
      country_settings: (prev.country_settings || DEFAULT_COUNTRY_SETTINGS).map((cs) =>
        cs.country_code === country_code ? { ...cs, [field]: numValue } : cs
      ),
    }));
  };

  const handleReset = () => {
    const fetchedCountrySettings: CountrySetting[] = settings.country_settings || [];
    const mergedCountrySettings = DEFAULT_COUNTRY_SETTINGS.map((def) => {
      const found = fetchedCountrySettings.find((c) => c.country_code === def.country_code);
      return found ? { ...def, ...found } : def;
    });

    setFormData({
      admin_commission_percentage: settings.admin_commission_percentage ?? "",
      customer_without_referral_discount: settings.customer_without_referral_discount ?? "",
      referral_person_first_ride_discount: settings.referral_person_first_ride_discount ?? "",
      tax_percentage: settings.tax_percentage ?? "",
      country_settings: mergedCountrySettings,
      is_referral_coupon_credit_disable: settings.is_referral_coupon_credit_disable ?? false,
    });
    toast.info("Form reverted to last saved settings");
  };

  const validateForm = (): string | null => {
    const {
      admin_commission_percentage,
      customer_without_referral_discount,
      referral_person_first_ride_discount,
      country_settings,
    } = formData;

    const currentAdminCommission =
      Number(
        admin_commission_percentage === ""
          ? settings.admin_commission_percentage
          : admin_commission_percentage
      ) || 15;
    const currentCustomerDiscount =
      Number(
        customer_without_referral_discount === ""
          ? settings.customer_without_referral_discount
          : customer_without_referral_discount
      ) || 10;
    const currentReferralDiscount =
      Number(
        referral_person_first_ride_discount === ""
          ? settings.referral_person_first_ride_discount
          : referral_person_first_ride_discount
      ) || 15;

    if (admin_commission_percentage !== undefined && admin_commission_percentage !== "") {
      const value = Number(admin_commission_percentage);
      if (isNaN(value) || value < 0 || value > 100)
        return "Admin commission percentage must be between 0 and 100";
      if (value < currentCustomerDiscount)
        return `Admin commission (${value}%) cannot be less than customer discount (${currentCustomerDiscount}%)`;
      if (value < currentReferralDiscount)
        return `Admin commission (${value}%) cannot be less than referral discount (${currentReferralDiscount}%)`;
    }

    if (
      customer_without_referral_discount !== undefined &&
      customer_without_referral_discount !== ""
    ) {
      const value = Number(customer_without_referral_discount);
      if (isNaN(value) || value < 0 || value > 100)
        return "Customer without referral discount must be between 0 and 100";
      if (value > currentAdminCommission)
        return `Customer discount (${value}%) cannot exceed admin commission (${currentAdminCommission}%)`;
    }

    if (
      referral_person_first_ride_discount !== undefined &&
      referral_person_first_ride_discount !== ""
    ) {
      const value = Number(referral_person_first_ride_discount);
      if (isNaN(value) || value < 0 || value > 100)
        return "Referral person first ride discount must be between 0 and 100";
      if (value > currentAdminCommission)
        return `Referral discount (${value}%) cannot exceed admin commission (${currentAdminCommission}%)`;
    }

    if (formData.tax_percentage !== undefined && formData.tax_percentage !== "") {
      const value = Number(formData.tax_percentage);
      if (isNaN(value) || value < 0 || value > 100)
        return "Tax percentage must be between 0 and 100";
    }

    for (const cs of country_settings || []) {
      const referralValue = cs.referral_amount === "" ? 0 : Number(cs.referral_amount);
      const withdrawValue =
        cs.minimum_withdraw_amount === "" ? 0 : Number(cs.minimum_withdraw_amount);

      if (isNaN(referralValue) || referralValue < 0 || referralValue > 100)
        return `Referral percentage for ${cs.country_code} must be between 0 and 100`;
      if (isNaN(withdrawValue) || withdrawValue < 0)
        return `Minimum withdraw amount for ${cs.country_code} must be a positive number`;
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) return toast.error(validationError, { autoClose: 7000 });

    try {
      setSaving(true);
      const updateData: SettingsData = {};

      if (
        formData.admin_commission_percentage !== undefined &&
        formData.admin_commission_percentage !== settings.admin_commission_percentage
      )
        updateData.admin_commission_percentage = formData.admin_commission_percentage;

      if (
        formData.customer_without_referral_discount !== undefined &&
        formData.customer_without_referral_discount !==
        settings.customer_without_referral_discount
      )
        updateData.customer_without_referral_discount =
          formData.customer_without_referral_discount;

      if (
        formData.referral_person_first_ride_discount !== undefined &&
        formData.referral_person_first_ride_discount !==
        settings.referral_person_first_ride_discount
      )
        updateData.referral_person_first_ride_discount =
          formData.referral_person_first_ride_discount;

      if (
        formData.tax_percentage !== undefined &&
        formData.tax_percentage !== settings.tax_percentage
      )
        updateData.tax_percentage =
          formData.tax_percentage === "" ? 0 : Number(formData.tax_percentage);

      updateData.country_settings = formData.country_settings;

      if (Object.keys(updateData).length === 0) {
        setSaving(false);
        return toast.info("No changes to update", { autoClose: 3000 });
      }

      await henceforthApi.Settings.updateSettings(updateData);
      setSaving(false);

      Swal.fire({
        title: "Success!",
        text: "Settings updated successfully",
        icon: "success",
        confirmButtonColor: "#006747",
        confirmButtonText: "OK",
      });

      fetchSettings();
    } catch (error: any) {
      setSaving(false);
      Swal.fire({
        title: "Error!",
        text: error.response?.body?.error_description || "Failed to update settings",
        icon: "error",
        confirmButtonColor: "#dc2626",
        confirmButtonText: "OK",
      });
    }
  };

  const handleReferralToggle = async () => {
    try {
      setReferralLoading(true);
      const newStatus = !formData.is_referral_coupon_credit_disable;

      await henceforthApi.Settings.update_all_users_referral_coupon_settings({
        is_referral_coupon_credit_disable: newStatus,
      });

      setFormData((prev) => ({
        ...prev,
        is_referral_coupon_credit_disable: newStatus,
      }));

      setReferralLoading(false);

      Swal.fire({
        title: "Success!",
        text: `Referral feature ${newStatus ? "disabled" : "enabled"} successfully`,
        icon: "success",
        confirmButtonColor: "#006747",
        confirmButtonText: "OK",
      });

      fetchSettings();
    } catch (error: any) {
      setReferralLoading(false);
      Swal.fire({
        title: "Error!",
        text: error.response?.body?.error_description || "Failed to update referral settings",
        icon: "error",
        confirmButtonColor: "#dc2626",
        confirmButtonText: "OK",
      });
    }
  };

  const isReferralActive = !formData.is_referral_coupon_credit_disable;

  return (
    <form onSubmit={handleSubmit} className="animated fadeInRight settings-page">

      {/* ── Section 1: Commission, Discount & Tax (All in one unified card) ── */}
      <div className="settings-card">
        <div className="settings-card-header">
          <div className="header-left">
            <div className="header-icon-wrap">
              <Sliders className="w-4 h-4" />
            </div>
            <div>
              <h4 className="header-title">Platform Rates &amp; Discounts</h4>
              <p className="header-description">
                Configure commission rates, passenger promotional discounts, and global tax
              </p>
            </div>
          </div>
        </div>

        <div className="settings-card-body space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

            {/* Admin Commission */}
            <div className="field-group">
              <label className="field-label">
                <span>
                  Admin Commission (Platform Fee)
                  <span className="required-star">*</span>
                </span>
              </label>
              <div className="input-affix-group">
                <input
                  type="number"
                  name="admin_commission_percentage"
                  className="affix-input w-full"
                  value={
                    formData.admin_commission_percentage === ""
                      ? ""
                      : formData.admin_commission_percentage
                  }
                  onChange={handleChange}
                  min="0"
                  max="100"
                  step="0.01"
                  placeholder="e.g. 15"
                  required
                />
                <span className="input-suffix">%</span>
              </div>
              <span className="field-hint">Platform cut per completed booking</span>
            </div>

            {/* First Ride Discount without Promo */}
            <div className="field-group">
              <label className="field-label">
                <span>
                  Standard 1st-Ride Discount
                  <span className="required-star">*</span>
                </span>
              </label>
              <div className="input-affix-group">
                <input
                  type="number"
                  name="customer_without_referral_discount"
                  className="affix-input w-full"
                  value={
                    formData.customer_without_referral_discount === ""
                      ? ""
                      : formData.customer_without_referral_discount
                  }
                  onChange={handleChange}
                  min="0"
                  max="100"
                  step="0.01"
                  placeholder="e.g. 10"
                  required
                />
                <span className="input-suffix">%</span>
              </div>
              <span className="field-hint">Discount on first ride without promo code</span>
            </div>

            {/* First Ride Discount with Promo */}
            <div className="field-group">
              <label className="field-label">
                <span>
                  Referral 1st-Ride Discount
                  <span className="required-star">*</span>
                </span>
              </label>
              <div className="input-affix-group">
                <input
                  type="number"
                  name="referral_person_first_ride_discount"
                  className="affix-input w-full"
                  value={
                    formData.referral_person_first_ride_discount === ""
                      ? ""
                      : formData.referral_person_first_ride_discount
                  }
                  onChange={handleChange}
                  min="0"
                  max="100"
                  step="0.01"
                  placeholder="e.g. 15"
                  required
                />
                <span className="input-suffix">%</span>
              </div>
              <span className="field-hint">Discount on first ride with referral code</span>
            </div>

            {/* Tax Percentage */}
            <div className="field-group">
              <label className="field-label">
                <span>Global Tax Rate</span>
              </label>
              <div className="input-affix-group">
                <input
                  type="number"
                  name="tax_percentage"
                  className="affix-input w-full"
                  value={formData.tax_percentage === "" ? "" : formData.tax_percentage}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  step="0.01"
                  placeholder="e.g. 5"
                />
                <span className="input-suffix">%</span>
              </div>
              <span className="field-hint">Tax percentage applied on ride bookings</span>
            </div>

          </div>

          {/* Pricing Guardrail Callout */}
          <div className="info-callout">
            <Info className="w-4 h-4 callout-icon" />
            <div className="callout-text">
              <strong>Pricing Rule:</strong> Passenger promotional discounts cannot exceed the Admin Commission Percentage.
            </div>
          </div>
        </div>
      </div>

      {/* ── Section 2: Referral Feature Master Switch (Clean banner, no double-wrapping) ── */}
      <div
        className={`referral-banner ${isReferralActive ? "status-enabled" : "status-disabled"
          }`}
      >
        <div className="banner-left">
          <div
            className={`banner-icon-wrap ${isReferralActive ? "icon-active" : "icon-inactive"
              }`}
          >
            <Gift className="w-5 h-5" />
          </div>
          <div>
            <div className="banner-title-row">
              <h5 className="banner-title">Referral &amp; Coupon Program</h5>
              <Badge variant={isReferralActive ? "success" : "destructive"}>
                {isReferralActive ? "Active" : "Disabled"}
              </Badge>
            </div>
            <p className="banner-subtitle">
              {isReferralActive
                ? "Referral coupons and credit generation are currently ENABLED for all users across the platform."
                : "Referral coupons and credit generation are currently DISABLED for all users."}
            </p>
          </div>
        </div>

        <div className="banner-right">
          <Button
            type="button"
            variant={isReferralActive ? "destructive" : "primary"}
            size="sm"
            onClick={handleReferralToggle}
            disabled={referralLoading}
            className="gap-2 font-semibold"
          >
            {referralLoading ? (
              <>
                <i className="fa fa-spinner fa-spin" />
                <span>Updating...</span>
              </>
            ) : isReferralActive ? (
              <>
                <ToggleRight className="w-4 h-4" />
                <span>Disable Feature</span>
              </>
            ) : (
              <>
                <ToggleLeft className="w-4 h-4" />
                <span>Enable Feature</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* ── Section 3: Country-Based Referral & Withdrawal ── */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-[#006747]" />
          <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider m-0">
            Regional Referral &amp; Withdrawal Settings
          </h4>
        </div>

        <div className="country-grid">
          {(formData.country_settings || DEFAULT_COUNTRY_SETTINGS).map((cs) => {
            const meta = COUNTRY_META[cs.country_code] || {
              flag: "🌐",
              name: cs.country_code,
              currency: "USD",
              currencySymbol: "$",
            };

            return (
              <div className="country-card" key={cs.country_code}>
                <div className="country-card-header">
                  <div className="country-flag-name">
                    <span className="country-flag">{meta.flag}</span>
                    <span className="country-name">{meta.name}</span>
                  </div>
                  <span className="country-currency-tag">{meta.currency}</span>
                </div>

                <div className="country-card-body">
                  {/* Referral Percentage */}
                  <div className="field-group">
                    <label className="field-label">
                      <span>
                        Referral Reward
                        <span className="required-star">*</span>
                      </span>
                    </label>
                    <div className="input-affix-group">
                      <input
                        type="number"
                        className="affix-input w-full"
                        value={cs.referral_amount === "" ? "" : cs.referral_amount}
                        onChange={(e) =>
                          handleCountryChange(
                            cs.country_code,
                            "referral_amount",
                            e.target.value
                          )
                        }
                        min="0"
                        max="100"
                        step="0.01"
                        placeholder="e.g. 10"
                        required
                      />
                      <span className="input-suffix">%</span>
                    </div>
                    <span className="field-hint">
                      Reward credited per referral
                    </span>
                  </div>

                  {/* Minimum Withdraw Amount */}
                  <div className="field-group">
                    <label className="field-label">
                      <span>
                        Minimum Payout
                        <span className="required-star">*</span>
                      </span>
                    </label>
                    <div className="input-affix-group">
                      <input
                        type="number"
                        className="affix-input affix-input-left w-full"
                        value={
                          cs.minimum_withdraw_amount === ""
                            ? ""
                            : cs.minimum_withdraw_amount
                        }
                        onChange={(e) =>
                          handleCountryChange(
                            cs.country_code,
                            "minimum_withdraw_amount",
                            e.target.value
                          )
                        }
                        min="0"
                        step="0.01"
                        placeholder="e.g. 50"
                        required
                      />
                      <span className="input-prefix">{meta.currencySymbol}</span>
                    </div>
                    <span className="field-hint">
                      Min wallet balance to withdraw
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Section 4: Action Footer ── */}
      <div className="action-footer">
        <div className="footer-note">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <span>Changes take effect immediately across all platforms upon saving</span>
        </div>

        <div className="footer-actions">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleReset}
            disabled={saving}
            className="gap-2 font-semibold"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Reset</span>
          </Button>

          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={saving}
            className="gap-2 font-bold min-w-[140px]"
          >
            {saving ? (
              <>
                <i className="fa fa-spinner fa-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Save Settings</span>
              </>
            )}
          </Button>
        </div>
      </div>

    </form>
  );
};

export default Settings;
